import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { t } from '../lib/translations';

export default function Footer() {
  const { language } = useAuth();

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { to: '/products', label: 'allProducts' },
    { to: '/orders', label: 'myOrders' },
    { to: '/profile', label: 'profile' },
  ];

  const supportLinks = [
    { to: '/about', label: language === 'hi' ? 'हमारे बारे में' : 'About Us' },
    { to: '/contact', label: language === 'hi' ? 'संपर्क करें' : 'Contact Us' },
    { to: '/faq', label: language === 'hi' ? 'सामान्य प्रश्न' : 'FAQs' },
  ];

  const policyLinks = [
    { to: '/privacy', label: language === 'hi' ? 'गोपनीयता नीति' : 'Privacy Policy' },
    { to: '/terms', label: language === 'hi' ? 'नियम व शर्तें' : 'Terms & Conditions' },
    { to: '/shipping', label: language === 'hi' ? 'शिपिंग नीति' : 'Shipping Policy' },
    { to: '/gst', label: language === 'hi' ? 'शिपिंग नीति' : 'GST Details' },
  ];

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      {/* Main Footer */}
      <div className="container-app py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">AV</span>
              </div>
              <div>
                <h3 className="font-bold">AV Mart</h3>
                <p className="text-xs text-slate-400">Shamli</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              {language === 'hi'
                ? 'शामली में आपका विश्वसनीय किराना और घरेलू सामान स्टोर। ताज़ी सब्जियां, डेयरी और रोज़मर्रा की ज़रूरतें।'
                : 'Your trusted grocery and household store in Shamli. Fresh daily essentials.'}
            </p>
                     </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">
              {language === 'hi' ? 'त्वरित लिंक' : 'Quick Links'}
            </h4>
            <ul className="space-y-2 text-sm text-slate-400">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} onClick={handleLinkClick} className="hover:text-white transition-colors">
                    {t(link.label, language)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold mb-4">
              {language === 'hi' ? 'सहायता' : 'Support'}
            </h4>
            <ul className="space-y-2 text-sm text-slate-400">
              {supportLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} onClick={handleLinkClick} className="hover:text-white transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <h4 className="font-semibold mb-2 text-white">
                {language === 'hi' ? 'नीतियां' : 'Policies'}
              </h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {policyLinks.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} onClick={handleLinkClick} className="hover:text-white transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold mb-4">
              {language === 'hi' ? 'संपर्क' : 'Contact'}
            </h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                <span>
                  {language === 'hi'
                    ? 'मुख्य बाज़ार, शामली, उत्तर प्रदेश - 247776'
                    : 'Shamli, Uttar Pradesh - 247776'}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-500 flex-shrink-0" />
                <a href="tel:+917251972000" className="hover:text-white transition-colors">
                  +91 72519 72000
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-green-500 flex-shrink-0" />
                <a href="mailto:contactus@avmartshamli.com" className="hover:text-white transition-colors">
                  contactus@avmartshamli.com
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-green-500 flex-shrink-0" />
                <span>{language === 'hi' ? 'सुबह 8 - रात 9 बजे' : '8 AM - 9 PM'}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-app py-4 flex flex-col md:flex-row items-center justify-between text-sm text-slate-500">
          <p>
            © {new Date().getFullYear()} AV Mart Shamli.{' '}
            {language === 'hi' ? 'सर्वाधिकार सुरक्षित।' : 'All rights reserved.'}
          </p>
          <p className="mt-2 md:mt-0">
            {language === 'hi' ? 'भारत में बनाया गया 🇮🇳' : 'Made in India'}
          </p>
        </div>
      </div>
    </footer>
  );
}
